import { el } from '../utils/dom.js';

/**
 * AutotileTemplate — Visual grid for mapping tiles to autotile positions.
 *
 * Displays a template grid where each cell represents a specific terrain shape
 * (center, edge-N, corner-NW, inner-corner-NW, narrow-H, etc.).
 * Users click a slot, then click a tile in the tileset to assign it.
 *
 * Three template sizes:
 *   - simple:  Just center + random variants (no edges)
 *   - edges:   3×3 grid (center, 4 edges, 4 outer corners) = 9 slots, + 4 inner corners = 13
 *   - full47:  Full blob-47 template, 47 slots organized visually
 *   - road:    Road network shapes (straight, curve, T, cross, dead-end) = 15 slots
 */

// Human-readable slot definitions for each template type.
// Each slot has: id (bitmask or name), label, row/col position in the visual grid,
// and a mini-diagram showing which neighbors are present.

const EDGE_SLOTS = [
  // 3×3 outer corners + edges + center
  { id: 'corner_nw', label: 'NW Corner',      row: 0, col: 0, diagram: '┌─' },
  { id: 'edge_n',    label: 'North Edge',     row: 0, col: 1, diagram: '──' },
  { id: 'corner_ne', label: 'NE Corner',      row: 0, col: 2, diagram: '─┐' },
  { id: 'edge_w',    label: 'West Edge',      row: 1, col: 0, diagram: '│ ' },
  { id: 'center',    label: 'Center',         row: 1, col: 1, diagram: '██' },
  { id: 'edge_e',    label: 'East Edge',      row: 1, col: 2, diagram: ' │' },
  { id: 'corner_sw', label: 'SW Corner',      row: 2, col: 0, diagram: '└─' },
  { id: 'edge_s',    label: 'South Edge',     row: 2, col: 1, diagram: '──' },
  { id: 'corner_se', label: 'SE Corner',      row: 2, col: 2, diagram: '─┘' },
  // Inner corners (concave) — separate row
  { id: 'inner_nw',  label: 'Inner NW',       row: 3, col: 0, diagram: '◜' },
  { id: 'inner_ne',  label: 'Inner NE',       row: 3, col: 1, diagram: '◝' },
  { id: 'inner_sw',  label: 'Inner SW',       row: 3, col: 2, diagram: '◟' },
  { id: 'inner_se',  label: 'Inner SE',       row: 4, col: 0, diagram: '◞' },
  // Narrow strips
  { id: 'narrow_h',  label: 'Narrow Horiz.',  row: 4, col: 1, diagram: '═' },
  { id: 'narrow_v',  label: 'Narrow Vert.',   row: 4, col: 2, diagram: '║' },
  // Isolated
  { id: 'isolated',  label: 'Single Tile',    row: 5, col: 1, diagram: '◻' },
];

const ROAD_SLOTS = [
  { id: 'straight_h',  label: 'Straight H',   row: 0, col: 0, diagram: '──' },
  { id: 'straight_v',  label: 'Straight V',   row: 0, col: 1, diagram: '│' },
  { id: 'curve_ne',    label: 'Curve NE',     row: 1, col: 0, diagram: '└' },
  { id: 'curve_nw',    label: 'Curve NW',     row: 1, col: 1, diagram: '┘' },
  { id: 'curve_se',    label: 'Curve SE',     row: 1, col: 2, diagram: '┌' },
  { id: 'curve_sw',    label: 'Curve SW',     row: 2, col: 0, diagram: '┐' },
  { id: 'tee_n',       label: 'T North',      row: 2, col: 1, diagram: '┴' },
  { id: 'tee_s',       label: 'T South',      row: 2, col: 2, diagram: '┬' },
  { id: 'tee_e',       label: 'T East',       row: 3, col: 0, diagram: '├' },
  { id: 'tee_w',       label: 'T West',       row: 3, col: 1, diagram: '┤' },
  { id: 'cross',       label: 'Crossroad',    row: 3, col: 2, diagram: '┼' },
  { id: 'end_n',       label: 'Dead-End N',   row: 4, col: 0, diagram: '╵' },
  { id: 'end_s',       label: 'Dead-End S',   row: 4, col: 1, diagram: '╷' },
  { id: 'end_e',       label: 'Dead-End E',   row: 4, col: 2, diagram: '╶' },
  { id: 'end_w',       label: 'Dead-End W',   row: 5, col: 0, diagram: '╴' },
];

// Map edge slot IDs → 4-bit bitmask (N=1, E=2, S=4, W=8) for the 4-bit system
const SLOT_TO_4BIT = {
  isolated: 0, edge_n: 4, edge_e: 8, edge_s: 1, edge_w: 2,
  corner_nw: 6, corner_ne: 12, corner_sw: 3, corner_se: 9,
  center: 15, narrow_h: 10, narrow_v: 5,
  // inner corners use the same bitmask space with flag
  inner_nw: 5, inner_ne: 5, inner_sw: 5, inner_se: 5,
};

// Map edge slot IDs → 8-bit blob bitmask (NW=1,N=2,NE=4,W=8,E=16,SW=32,S=64,SE=128)
const SLOT_TO_BLOB47 = {
  isolated: 0,
  edge_n: 64,         // S only (terrain is south of this tile)
  edge_s: 2,          // N only
  edge_w: 16,         // E only
  edge_e: 8,          // W only
  corner_nw: 80,      // E+S
  corner_ne: 72,      // W+S
  corner_sw: 18,      // N+E
  corner_se: 10,      // N+W
  center: 255,        // all neighbors
  inner_nw: 214,      // all except NW area: N+NE+E+S+SE
  inner_ne: 107,      // all except NE area: NW+N+W+SW+S
  inner_sw: 22,       // N+NE+E (terrain wraps around NE)
  inner_se: 11,       // NW+N+W
  narrow_h: 24,       // W+E
  narrow_v: 66,       // N+S
};

export class AutotileTemplate {
  /**
   * @param {Object} options
   * @param {string} options.format — 'simple' | 'edges' | 'full47' | 'road'
   * @param {HTMLImageElement} options.tilesetImage
   * @param {number} options.tileSize — pixel size of tiles in the tileset
   * @param {number} options.columns — tileset columns
   * @param {function} options.onSlotAssigned — callback(slotId, tileId)
   * @param {Object} [options.existing] — existing slot→tileId assignments
   */
  constructor(options) {
    this.format = options.format || 'edges';
    this.tilesetImage = options.tilesetImage;
    this.tileSize = options.tileSize;
    this.columns = options.columns;
    this.onSlotAssigned = options.onSlotAssigned || (() => {});
    this.assignments = new Map(Object.entries(options.existing || {}));
    this.activeSlot = null; // currently selected slot waiting for tile assignment
    this._element = null;
  }

  getSlots() {
    if (this.format === 'road') return ROAD_SLOTS;
    if (this.format === 'simple') return [{ id: 'center', label: 'Center', row: 0, col: 0, diagram: '██' }];
    return EDGE_SLOTS; // edges and full47 both use EDGE_SLOTS (full47 is just more populated)
  }

  /** Assign a tile ID from the tileset to the currently active slot. */
  assignTile(tileId) {
    if (this.activeSlot == null) return false;
    this.assignments.set(this.activeSlot, tileId);
    this.onSlotAssigned(this.activeSlot, tileId);
    const prev = this.activeSlot;
    this.activeSlot = null;
    this._renderSlots();
    return true;
  }

  /** Has an active slot waiting for a tile? */
  isWaitingForTile() {
    return this.activeSlot != null;
  }

  /** Get all assignments as { slotId: tileId } */
  getAssignments() {
    return Object.fromEntries(this.assignments);
  }

  /** Build bitmask→tileId mapping for use in TerrainGroup.variants */
  toBitmaskMap() {
    const map = {};
    const lookup = this.format === 'road' ? null : SLOT_TO_BLOB47;
    for (const [slotId, tileId] of this.assignments) {
      if (slotId === 'center') continue; // center is stored separately
      if (lookup && lookup[slotId] != null) {
        map[lookup[slotId]] = tileId;
      } else {
        // Road uses named keys directly
        map[slotId.toUpperCase()] = tileId;
      }
    }
    return map;
  }

  /** Render the template into a container element. */
  render() {
    const wrap = el('div', { className: 'autotile-template' });

    // Instructions
    const hint = el('div', { className: 'autotile-hint' },
      this.activeSlot
        ? `Click a tile in the tileset to assign it to "${this._slotLabel(this.activeSlot)}"`
        : 'Click a slot below, then click the matching tile in your tileset.',
    );
    if (this.activeSlot) hint.classList.add('autotile-hint-active');
    wrap.appendChild(hint);

    // Grid of slots
    const grid = el('div', { className: 'autotile-grid', id: 'autotileGrid' });
    this._renderSlotsInto(grid);
    wrap.appendChild(grid);

    this._element = wrap;
    return wrap;
  }

  _renderSlots() {
    const grid = this._element?.querySelector('#autotileGrid');
    if (!grid) return;
    grid.innerHTML = '';
    this._renderSlotsInto(grid);
    // Update hint
    const hint = this._element?.querySelector('.autotile-hint');
    if (hint) {
      hint.textContent = this.activeSlot
        ? `Click a tile in the tileset to assign it to "${this._slotLabel(this.activeSlot)}"`
        : 'Click a slot below, then click the matching tile in your tileset.';
      hint.classList.toggle('autotile-hint-active', !!this.activeSlot);
    }
  }

  _renderSlotsInto(grid) {
    const slots = this.getSlots();

    // Determine grid dimensions
    let maxRow = 0, maxCol = 0;
    for (const s of slots) {
      if (s.row > maxRow) maxRow = s.row;
      if (s.col > maxCol) maxCol = s.col;
    }
    grid.style.gridTemplateColumns = `repeat(${maxCol + 1}, 1fr)`;

    // Section labels
    const sections = new Map();
    if (this.format === 'edges' || this.format === 'full47') {
      sections.set(0, 'Outer edges & corners');
      sections.set(3, 'Inner corners (concave)');
      sections.set(4, 'Narrow strips');
      sections.set(5, 'Isolated');
    }

    let lastSection = -1;
    for (const slot of slots) {
      // Section header
      if (sections.has(slot.row) && slot.row !== lastSection) {
        lastSection = slot.row;
        const label = el('div', {
          className: 'autotile-section-label',
          style: { gridColumn: `1 / ${maxCol + 2}` },
        }, sections.get(slot.row));
        grid.appendChild(label);
      }

      const tileId = this.assignments.get(slot.id);
      const isActive = this.activeSlot === slot.id;
      const isAssigned = tileId != null;

      const cell = el('div', {
        className: `autotile-slot ${isActive ? 'active' : ''} ${isAssigned ? 'assigned' : ''}`,
        onClick: () => {
          this.activeSlot = this.activeSlot === slot.id ? null : slot.id;
          this._renderSlots();
        },
      });

      // Tile preview or diagram
      if (isAssigned) {
        const preview = this._renderTilePreview(tileId);
        cell.appendChild(preview);
      } else {
        cell.appendChild(el('span', { className: 'autotile-diagram' }, slot.diagram));
      }

      cell.appendChild(el('span', { className: 'autotile-slot-label' }, slot.label));

      grid.appendChild(cell);
    }
  }

  _renderTilePreview(tileId) {
    const canvas = el('canvas', { className: 'autotile-preview' });
    const size = 32; // preview size
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = false;

    const col = tileId % this.columns;
    const row = Math.floor(tileId / this.columns);
    const sx = col * this.tileSize;
    const sy = row * this.tileSize;

    ctx.drawImage(this.tilesetImage, sx, sy, this.tileSize, this.tileSize, 0, 0, size, size);
    return canvas;
  }

  _slotLabel(slotId) {
    const slots = this.getSlots();
    const slot = slots.find(s => s.id === slotId);
    return slot ? slot.label : slotId;
  }
}
